import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Pencil } from 'lucide-react';
import { SIZE_CLASSES, createVehicleSchema } from '@wrapforce/shared';
import type { ModifierKey, SizeClass, VehicleCatalogTrimDetail, WorkspaceVehicleListItem, WorkspaceVehicleListResponse } from '@wrapforce/shared';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';
import { useDebounce } from '@/lib/useDebounce';
import { Input, Label, FieldError } from '@/components/ui/input';
import { AutocompleteInput } from '@/components/ui/autocomplete-input';
import { Button } from '@/components/ui/button';
import { useCreateVehicle } from '@/features/customers/vehiclesApi';
import { useCreateWorkspaceVehicle, useUpdateWorkspaceVehicle, useVehicleCatalog, useWorkspaceVehicles } from './pricingApi';

// Backs the Make/Model AutocompleteInput suggestions on both AddVehicleDialog
// (NewQuotePage.tsx) and CustomVehicleForm below - reuses the same platform
// vehicle_catalog search the trim picker above already queries, so no new
// backend endpoint is needed for a make/model autocomplete.
export function useVehicleCatalogSuggestions(workspaceId: string | null, make: string, model: string) {
  const debouncedMake = useDebounce(make.trim(), 300);
  const debouncedModel = useDebounce(model.trim(), 300);

  const { data: makeData } = useVehicleCatalog(workspaceId, {
    make: debouncedMake || undefined,
    limit: 50,
    enabled: debouncedMake.length >= 1,
  });
  const { data: modelData } = useVehicleCatalog(workspaceId, {
    make: debouncedMake || undefined,
    model: debouncedModel || undefined,
    limit: 50,
    enabled: debouncedModel.length >= 1,
  });

  const makeOptions = Array.from(new Set((makeData?.items ?? []).map((i) => i.make))).sort();
  const modelOptions = Array.from(new Set((modelData?.items ?? []).map((i) => i.model))).sort();
  return { makeOptions, modelOptions };
}

export interface VehicleSelection {
  sizeClass: SizeClass;
  baseFtX10: number;
  baseMinutes: number;
  complexityBp: number;
  modifiers: ModifierKey[];
  catalogVehicleId: string | null;
  catalogTrimId: string | null;
  workspaceVehicleId: string | null;
  label: string;
  // §34.1/§35 — this vehicle's own effective L1/L2/L3 disassembly minutes
  // (catalog default for a catalog trim, the shop's own numbers for a custom
  // vehicle) - fed into resolveDisassemblyMinutesByLevel, which now uses these
  // verbatim with no shop-wide profile fallback.
  l1DisassemblyMinutes: number;
  l2DisassemblyMinutes: number;
  l3DisassemblyMinutes: number;
}

// Translation keys (not literal labels, since this module has no access to
// react-i18next's t() at module scope) — consumed both by the <option>
// labels below and by NewQuotePage's line summary.
export const SIZE_CLASS_LABEL_KEYS: Record<SizeClass, string> = {
  city_compact: 'pricing.sizeClass.city_compact',
  sedan: 'pricing.sizeClass.sedan',
  hatch_wagon: 'pricing.sizeClass.hatch_wagon',
  coupe_sports: 'pricing.sizeClass.coupe_sports',
  crossover_mid_suv: 'pricing.sizeClass.crossover_mid_suv',
  full_suv: 'pricing.sizeClass.full_suv',
  pickup_truck: 'pricing.sizeClass.pickup_truck',
  van_commercial: 'pricing.sizeClass.van_commercial',
  exotic_supercar: 'pricing.sizeClass.exotic_supercar',
};

// Kept alongside SIZE_CLASS_LABEL_KEYS for settings/pricing-v2 consumers
// (PpfTab.tsx, WrapTab.tsx) outside this translation pass's scope.
export const SIZE_CLASS_LABELS: Record<SizeClass, string> = {
  city_compact: 'City / compact',
  sedan: 'Sedan',
  hatch_wagon: 'Hatch / wagon',
  coupe_sports: 'Coupe / sports',
  crossover_mid_suv: 'Crossover / mid SUV',
  full_suv: 'Full-size SUV',
  pickup_truck: 'Pickup truck',
  van_commercial: 'Van / commercial',
  exotic_supercar: 'Exotic / supercar',
};

// Typical job size per size class, averaged from this workspace's own vehicle
// catalog (supabase/migrations/0014_pricing_engine_seed.sql). Used to auto-fill
// the custom-vehicle form so a new user never has to guess a raw ft/minutes/
// complexity number — same hardcoding rationale as MODIFIER_DEFS in
// VehicleAwareWrapLineBuilder.tsx (no endpoint exposes these averages).
export const SIZE_CLASS_DEFAULTS: Record<SizeClass, { baseFtX10: number; baseMinutes: number; complexityBp: number }> = {
  city_compact: { baseFtX10: 520, baseMinutes: 1200, complexityBp: 10000 },
  sedan: { baseFtX10: 600, baseMinutes: 1500, complexityBp: 10200 },
  hatch_wagon: { baseFtX10: 580, baseMinutes: 1440, complexityBp: 10200 },
  coupe_sports: { baseFtX10: 580, baseMinutes: 1560, complexityBp: 11400 },
  crossover_mid_suv: { baseFtX10: 700, baseMinutes: 1680, complexityBp: 10100 },
  full_suv: { baseFtX10: 850, baseMinutes: 2100, complexityBp: 10700 },
  pickup_truck: { baseFtX10: 800, baseMinutes: 1920, complexityBp: 10300 },
  van_commercial: { baseFtX10: 950, baseMinutes: 2160, complexityBp: 9000 },
  exotic_supercar: { baseFtX10: 600, baseMinutes: 2400, complexityBp: 14800 },
};

// Difficulty is a multiplier on top of the picked size class's own average
// complexity (SIZE_CLASS_DEFAULTS above), so "average" always means "average
// for this kind of vehicle" rather than a fixed number nobody can eyeball.
// Presets exist because a raw decimal ("1.15") means nothing to someone
// filling in a form — these labels are what a shop can actually judge.
export const DIFFICULTY_PRESETS = [
  { key: 'much_easier', multiplier: 0.7 },
  { key: 'easier', multiplier: 0.85 },
  { key: 'average', multiplier: 1 },
  { key: 'harder', multiplier: 1.15 },
  { key: 'much_harder', multiplier: 1.4 },
] as const;
export type DifficultyPresetKey = (typeof DIFFICULTY_PRESETS)[number]['key'];

// A shop's own custom vehicle (created via "Vehicle not listed?", listed here
// via GET /pricing-vehicles) has no trims to pick between - the whole row is
// the selection. Exported so a saved quote line's editor can rebuild the same
// selection from a single-vehicle GET (useWorkspaceVehicleById) instead of
// only ever being reachable by picking it fresh from the search list.
export function vehicleSelectionFromWorkspaceVehicleItem(vehicle: WorkspaceVehicleListItem): VehicleSelection {
  return {
    sizeClass: vehicle.sizeClass,
    baseFtX10: vehicle.baseFtX10,
    baseMinutes: vehicle.baseMinutes,
    complexityBp: vehicle.complexityBp,
    modifiers: [],
    catalogVehicleId: null,
    catalogTrimId: null,
    workspaceVehicleId: vehicle.id,
    label: vehicle.label,
    l1DisassemblyMinutes: vehicle.l1DisassemblyMinutes,
    l2DisassemblyMinutes: vehicle.l2DisassemblyMinutes,
    l3DisassemblyMinutes: vehicle.l3DisassemblyMinutes,
  };
}

// For a saved quote line's editor rebuilding a
// catalog trim selection from a single-trim GET (useVehicleCatalogTrimDetail)
// instead of only ever being reachable by picking it fresh from the search
// list - the detail response already carries the resolved (override-applied)
// values, so no additive-delta math is needed here.
export function vehicleSelectionFromTrimDetail(detail: VehicleCatalogTrimDetail): VehicleSelection {
  return {
    sizeClass: detail.sizeClass,
    baseFtX10: detail.baseFtX10,
    baseMinutes: detail.baseMinutes,
    complexityBp: detail.complexityBp,
    modifiers: detail.modifiers,
    catalogVehicleId: detail.catalogVehicleId,
    catalogTrimId: detail.catalogTrimId,
    workspaceVehicleId: null,
    label: `${detail.make} ${detail.model} — ${detail.trimName}`,
    l1DisassemblyMinutes: detail.l1DisassemblyMinutes,
    l2DisassemblyMinutes: detail.l2DisassemblyMinutes,
    l3DisassemblyMinutes: detail.l3DisassemblyMinutes,
  };
}

// §34.1/§35 — same clamped methodology as the vehicle_catalog seed backfill
// (supabase/migrations/0039_vehicle_disassembly_schema.sql /
// 0048_vehicle_disassembly_levels.sql, D-34-01) so a shop-created custom
// vehicle gets a disassembly estimate consistent with the platform catalog's,
// scaled off the same difficulty-adjusted complexityBp the rest of this form
// already computes.
export function estimateDisassemblyMinutes(complexityBp: number): {
  l1DisassemblyMinutes: number;
  l2DisassemblyMinutes: number;
  l3DisassemblyMinutes: number;
} {
  const roundTo30 = (n: number) => Math.round(n / 30) * 30;
  const roundTo15 = (n: number) => Math.round(n / 15) * 15;
  const l1 = Math.min(360, Math.max(60, roundTo15((180 * complexityBp) / 10000)));
  const l2 = Math.min(900, Math.max(120, roundTo30((480 * complexityBp) / 10000)));
  const l3 = Math.min(1800, Math.max(240, roundTo30((900 * complexityBp) / 10000)));
  return { l1DisassemblyMinutes: l1, l2DisassemblyMinutes: l2, l3DisassemblyMinutes: l3 };
}

export interface CustomerVehicleHint {
  year: number | null;
  make: string;
  model: string;
  // The customer's actual vehicle record id and paint colour, when known -
  // used by PrintLineBuilder's "create a design" redirect to prefill the new
  // printed-wrap design's vehicle photo picker and colour field.
  id?: string | null;
  color?: string | null;
}

function hintQuery(hint: CustomerVehicleHint | null): string {
  return hint ? [hint.make, hint.model].filter(Boolean).join(' ').trim() : '';
}

export function VehiclePicker({
  workspaceId, selection, onSelect, customerVehicleHint = null, customerId = null, priceableSizeClasses,
}: {
  workspaceId: string | null;
  selection: VehicleSelection | null;
  onSelect: (selection: VehicleSelection) => void;
  // The customer's own vehicle, already captured in step 2 — used to pre-fill
  // the search here so the shop doesn't have to type the same make/model
  // twice. Still just a starting point: the trim/pricing match is a separate
  // concept (vehicle_catalog) from the customer's vehicle record, so the shop
  // confirms it with one click rather than it being silently auto-picked.
  customerVehicleHint?: CustomerVehicleHint | null;
  // The customer this quote is for - passed through to CustomVehicleForm so
  // "Vehicle not listed?" can also save a real vehicle to the customer's
  // record, not just a workspace pricing entry (§ vehicle-not-listed bug fix).
  customerId?: string | null;
  // When provided, hides vehicles/trims whose size class has no pricing row
  // for the currently selected package/zone/option, so the shop never picks
  // a vehicle the "Add this service" button will then refuse. Omitted by
  // VehicleAwareWrapLineBuilder.tsx, whose blocker is material stock, not
  // size class.
  priceableSizeClasses?: Set<SizeClass>;
}) {
  const { t } = useTranslation('quotes');
  const [search, setSearch] = useState(() => hintQuery(customerVehicleHint));
  const debouncedSearch = useDebounce(search, 300);
  const [editOpen, setEditOpen] = useState(false);

  // customerVehicleHint often arrives after this component's first render
  // (it's a separate fetch) — fill the search box once it lands, but only if
  // the shop hasn't already selected a vehicle or typed their own search.
  useEffect(() => {
    if (!selection && search === '' && customerVehicleHint) {
      setSearch(hintQuery(customerVehicleHint));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerVehicleHint?.make, customerVehicleHint?.model]);

  const searchReady = debouncedSearch.length >= 2;
  // Only the shop's own custom vehicles are pickable here — the platform
  // vehicle_catalog is no longer listed (it lacked the year, couldn't be
  // edited, and its trims weren't the shop's own numbers). The catalog still
  // powers the make/model autocomplete in CustomVehicleForm below.
  const { data: workspaceVehicles, isFetching: isFetchingWorkspaceVehicles } = useWorkspaceVehicles(workspaceId, {
    q: searchReady ? debouncedSearch : undefined,
    enabled: searchReady,
  });

  const noRawMatches = searchReady && !isFetchingWorkspaceVehicles
    && (workspaceVehicles?.items.length ?? 0) === 0;

  const filteredWorkspaceVehicles = priceableSizeClasses
    ? (workspaceVehicles?.items ?? []).filter((v) => priceableSizeClasses.has(v.sizeClass))
    : (workspaceVehicles?.items ?? []);
  const noPriceableVehicles = !!priceableSizeClasses && !noRawMatches
    && searchReady && !isFetchingWorkspaceVehicles
    && filteredWorkspaceVehicles.length === 0;

  // No matching custom vehicle for this search → the add-vehicle form shows
  // automatically (no "Vehicle not listed?" button to click anymore).
  const customOpen = noRawMatches && !selection;

  return (
    <div className="flex flex-col gap-3">
      {customerVehicleHint && !selection ? (
        <p className="text-xs text-muted">{t('pricing.vehiclePicker.matchingHint')}</p>
      ) : null}
      <Input
        placeholder={t('pricing.vehiclePicker.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {selection ? (
        <div className="flex items-center justify-between gap-2 border border-accent bg-accent/10 px-3 py-2 text-sm font-semibold">
          <span>{selection.label}</span>
          <span className="ml-auto text-xs font-normal text-muted">{t(SIZE_CLASS_LABEL_KEYS[selection.sizeClass])}</span>
          {selection.workspaceVehicleId ? (
            <button
              type="button"
              onClick={() => setEditOpen((v) => !v)}
              aria-label={t('pricing.vehiclePicker.editVehicle')}
              className="grid h-7 w-7 shrink-0 place-items-center text-muted hover:bg-surface-2 hover:text-ink"
            >
              <Pencil size={14} />
            </button>
          ) : null}
        </div>
      ) : null}

      {editOpen && selection?.workspaceVehicleId ? (
        <CustomVehicleForm
          workspaceId={workspaceId}
          customerId={customerId}
          onCreated={(next) => {
            setEditOpen(false);
            onSelect(next);
          }}
          customerVehicleHint={customerVehicleHint}
          editing={selection}
        />
      ) : customOpen ? (
        <CustomVehicleForm
          workspaceId={workspaceId}
          customerId={customerId}
          onCreated={onSelect}
          customerVehicleHint={customerVehicleHint}
          autoOpenedFromMissingHint={!!customerVehicleHint}
        />
      ) : (
        <div className="flex max-h-72 flex-col gap-2 overflow-y-auto">
          {!searchReady ? <p className="text-sm text-muted">{t('pricing.vehiclePicker.startTyping')}</p> : null}
          {searchReady && isFetchingWorkspaceVehicles ? <p className="text-sm text-muted">{t('pricing.vehiclePicker.searching')}</p> : null}
          {noPriceableVehicles ? <p className="text-sm text-muted">{t('pricing.vehiclePicker.noPriceableVehicles')}</p> : null}
          {filteredWorkspaceVehicles
            // The selected vehicle is already shown in the banner above -
            // repeating it in the results list read as a duplicate entry.
            .filter((vehicle) => vehicle.id !== selection?.workspaceVehicleId)
            .map((vehicle) => (
              <button
                key={vehicle.id}
                type="button"
                onClick={() => onSelect(vehicleSelectionFromWorkspaceVehicleItem(vehicle))}
                className="flex items-center justify-between border border-border p-2.5 text-left text-sm font-semibold hover:bg-surface-2"
              >
                <span>{vehicle.label}</span>
                <span className="text-xs font-normal text-muted">{t(SIZE_CLASS_LABEL_KEYS[vehicle.sizeClass])}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

// Best-effort split of a saved custom vehicle's single label ("2023 Tesla
// Model 3") back into the form's year/make/model fields for editing.
function parseVehicleLabel(label: string): { year: number | null; make: string; model: string } {
  const m = label.trim().match(/^(\d{4})\s+(\S+)\s*(.*)$/);
  if (m) return { year: Number(m[1]), make: m[2] ?? '', model: m[3] ?? '' };
  const [make = '', ...rest] = label.trim().split(/\s+/);
  return { year: null, make, model: rest.join(' ') };
}

// Reverse of the complexityBp computation below - which difficulty preset
// produces this stored complexityBp for this size class (average if none).
function difficultyFromComplexity(sizeClass: SizeClass, complexityBp: number): DifficultyPresetKey {
  return DIFFICULTY_PRESETS.find((p) => Math.round(SIZE_CLASS_DEFAULTS[sizeClass].complexityBp * p.multiplier) === complexityBp)?.key ?? 'average';
}

function CustomVehicleForm({
  workspaceId, customerId, onCreated, customerVehicleHint, autoOpenedFromMissingHint = false, editing = null,
}: {
  workspaceId: string | null;
  customerId: string | null;
  onCreated: (selection: VehicleSelection) => void;
  customerVehicleHint: CustomerVehicleHint | null;
  // True when this form was opened automatically because the customer's
  // hinted vehicle had zero matches - shows an extra explanatory notice.
  autoOpenedFromMissingHint?: boolean;
  // When set (a selected custom vehicle), the form edits that workspace
  // vehicle in place (PATCH) instead of creating a new one.
  editing?: VehicleSelection | null;
}) {
  const { t } = useTranslation('quotes');
  const yearId = useId();
  const makeId = useId();
  const modelId = useId();
  const vinId = useId();
  const createVehicle = useCreateWorkspaceVehicle(workspaceId);
  const updateVehicle = useUpdateWorkspaceVehicle(workspaceId);
  const createCustomerVehicle = useCreateVehicle(workspaceId, customerId ?? '');
  const editingParsed = editing ? parseVehicleLabel(editing.label) : null;
  const [year, setYear] = useState<number | null>(editingParsed?.year ?? customerVehicleHint?.year ?? null);
  const [make, setMake] = useState(editingParsed?.make ?? customerVehicleHint?.make ?? '');
  const [model, setModel] = useState(editingParsed?.model ?? customerVehicleHint?.model ?? '');
  // The VIN belongs to the customer's vehicle record, so it's only offered when
  // this form will create one (a new custom vehicle for a known customer) - not
  // when editing an existing workspace pricing vehicle.
  const showVin = !!customerId && !editing;
  const [vin, setVin] = useState('');
  const vinError = vin.trim() !== '' && !createVehicleSchema.shape.vin.safeParse(vin.trim().toUpperCase()).success;
  const label = [year, make, model].filter(Boolean).join(' ').trim();
  const { makeOptions, modelOptions } = useVehicleCatalogSuggestions(workspaceId, make, model);
  const [sizeClass, setSizeClass] = useState<SizeClass>(editing?.sizeClass ?? 'sedan');

  // Deliberately empty at first — the shop must fill in its own numbers
  // before saving; nothing is auto-filled from the size class anymore.
  // (Prefilled when editing an existing custom vehicle.)
  const [baseFtX10, setBaseFtX10] = useState<number | null>(editing?.baseFtX10 ?? null);
  const [baseMinutes, setBaseMinutes] = useState<number | null>(editing?.baseMinutes ?? null);
  const [difficultyPreset, setDifficultyPreset] = useState<DifficultyPresetKey | ''>(
    editing ? difficultyFromComplexity(editing.sizeClass, editing.complexityBp) : '',
  );
  const complexityBp = difficultyPreset === ''
    ? SIZE_CLASS_DEFAULTS[sizeClass].complexityBp
    : Math.round(SIZE_CLASS_DEFAULTS[sizeClass].complexityBp * DIFFICULTY_PRESETS.find((p) => p.key === difficultyPreset)!.multiplier);

  const complete = year !== null && make.trim() !== '' && model.trim() !== ''
    && baseFtX10 !== null && baseFtX10 > 0
    && baseMinutes !== null && baseMinutes > 0
    && difficultyPreset !== '';

  async function handleCreate() {
    if (!workspaceId || !complete || vinError || baseFtX10 === null || baseMinutes === null) return;
    const { l1DisassemblyMinutes, l2DisassemblyMinutes, l3DisassemblyMinutes } = estimateDisassemblyMinutes(complexityBp);
    const fields = {
      label: label.trim(),
      sizeClass,
      baseFtX10,
      baseMinutes,
      complexityBp,
      isCustom: true,
      l1DisassemblyMinutes,
      l2DisassemblyMinutes,
      l3DisassemblyMinutes,
    };

    let workspaceVehicleId: string;
    if (editing?.workspaceVehicleId) {
      await updateVehicle.mutateAsync({ vehicleId: editing.workspaceVehicleId, input: fields });
      workspaceVehicleId = editing.workspaceVehicleId;
    } else {
      // Dedupe guard: if a custom vehicle with this exact label already
      // exists, reuse it instead of creating a second identical row.
      const existing = await api.get<WorkspaceVehicleListResponse>(workspacePath(workspaceId, '/pricing-vehicles'), { query: { q: label.trim() } });
      const match = existing.items.find((v) => v.label.trim().toLowerCase() === label.trim().toLowerCase());
      if (match) {
        onCreated(vehicleSelectionFromWorkspaceVehicleItem(match));
        return;
      }
      const created = await createVehicle.mutateAsync(fields);
      workspaceVehicleId = created.id;
      // This is a real vehicle for this customer (not just a one-off pricing
      // entry), so save it to their vehicle record too - otherwise it would
      // never show up in step 2's saved-vehicle list next time. Skipped when
      // the step-2 hint already matches what was typed - that vehicle is
      // already on the customer's record, and re-adding it here was creating
      // a duplicate in the customer profile.
      const hintMatches = !!customerVehicleHint
        && (customerVehicleHint.year ?? null) === year
        && customerVehicleHint.make.trim().toLowerCase() === make.trim().toLowerCase()
        && customerVehicleHint.model.trim().toLowerCase() === model.trim().toLowerCase();
      if (customerId && !hintMatches) {
        await createCustomerVehicle.mutateAsync({
          year: year ?? undefined,
          make: make.trim() || undefined,
          model: model.trim() || undefined,
          vin: vin.trim() ? vin.trim().toUpperCase() : undefined,
        });
      }
    }

    onCreated({
      sizeClass,
      baseFtX10,
      baseMinutes,
      complexityBp,
      modifiers: [],
      catalogVehicleId: null,
      catalogTrimId: null,
      workspaceVehicleId,
      label: label.trim(),
      l1DisassemblyMinutes,
      l2DisassemblyMinutes,
      l3DisassemblyMinutes,
    });
  }

  return (
    <div className="flex flex-col gap-3 border border-dashed border-border p-3">
      <div className="grid grid-cols-3 gap-2.5">
        <div className="flex flex-col gap-1">
          <Label htmlFor={yearId}>{t('newQuote.addVehicleDialog.year')}</Label>
          <Input
            id={yearId}
            type="number"
            value={year ?? ''}
            onChange={(e) => setYear(e.target.value === '' ? null : Number(e.target.value))}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={makeId}>{t('newQuote.addVehicleDialog.make')}</Label>
          <AutocompleteInput id={makeId} value={make} onChange={setMake} options={makeOptions} />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={modelId}>{t('newQuote.addVehicleDialog.model')}</Label>
          <AutocompleteInput id={modelId} value={model} onChange={setModel} options={modelOptions} />
        </div>
      </div>
      {showVin ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={vinId}>{t('newQuote.addVehicleDialog.vin')}</Label>
          <Input id={vinId} value={vin} onChange={(e) => setVin(e.target.value)} placeholder={t('newQuote.addVehicleDialog.vinPlaceholder')} />
          {vinError ? <FieldError>{t('newQuote.addVehicleDialog.vinError')}</FieldError> : null}
        </div>
      ) : null}
      <div className="flex flex-col gap-1">
        <Label htmlFor="cv-size">{t('pricing.vehiclePicker.customForm.sizeClassLabel')}</Label>
        <select
          id="cv-size"
          value={sizeClass}
          onChange={(e) => setSizeClass(e.target.value as SizeClass)}
          className="h-9 border border-border bg-surface px-2 text-sm"
        >
          {SIZE_CLASSES.map((sc) => (
            <option key={sc} value={sc}>{t(SIZE_CLASS_LABEL_KEYS[sc])}</option>
          ))}
        </select>
      </div>

      {autoOpenedFromMissingHint && customerVehicleHint ? (
        <div className="flex items-start gap-2.5 border border-accent/30 bg-accent/10 px-3.5 py-3 text-sm">
          <Info size={16} className="mt-0.5 shrink-0" />
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold">{t('pricing.vehiclePicker.customForm.autoOpenTitle')}</span>
            <span>
              {t('pricing.vehiclePicker.customForm.autoOpenBody', {
                vehicle: [customerVehicleHint.year, customerVehicleHint.make, customerVehicleHint.model].filter(Boolean).join(' ').trim(),
              })}
            </span>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-2.5 border-t border-border pt-3">
        <p className="text-xs font-semibold text-muted">{t('pricing.vehiclePicker.customForm.fineTune')}</p>
        <div className="grid grid-cols-3 gap-2.5">
          <div className="flex flex-col gap-1">
            <Label htmlFor="cv-ft">{t('pricing.vehiclePicker.customForm.filmNeeded')}</Label>
            <Input
              id="cv-ft"
              type="number"
              min={0}
              value={baseFtX10 === null ? '' : baseFtX10 / 10}
              onChange={(e) => setBaseFtX10(e.target.value === '' ? null : Math.round(Math.max(0, Number(e.target.value)) * 10))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="cv-min">{t('pricing.vehiclePicker.customForm.laborTime')}</Label>
            <Input
              id="cv-min"
              type="number"
              min={0}
              step={0.5}
              value={baseMinutes === null ? '' : baseMinutes / 60}
              onChange={(e) => setBaseMinutes(e.target.value === '' ? null : Math.round(Math.max(0, Number(e.target.value)) * 60))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="cv-complexity">{t('pricing.vehiclePicker.customForm.difficulty')}</Label>
            <select
              id="cv-complexity"
              value={difficultyPreset}
              onChange={(e) => setDifficultyPreset(e.target.value as DifficultyPresetKey | '')}
              className="h-9 border border-border bg-surface px-2 text-sm"
            >
              <option value="" disabled>{t('pricing.vehiclePicker.customForm.difficultyPlaceholder')}</option>
              {DIFFICULTY_PRESETS.map((p) => (
                <option key={p.key} value={p.key}>{t(`pricing.vehiclePicker.difficultyPreset.${p.key}`)}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <Button type="button" size="sm" loading={createVehicle.isPending || updateVehicle.isPending || createCustomerVehicle.isPending} disabled={!complete || vinError} onClick={handleCreate}>
        {editing ? t('pricing.saveChanges') : t('pricing.vehiclePicker.customForm.save')}
      </Button>
    </div>
  );
}
